import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { createRoot, Root } from 'react-dom/client';
import * as React from 'react';

const TuhonoFilterPanel = require('@react/components/tuhono-filters/TuhonoFilterPanel').default;

export interface TuhonoFilterOption {
  key: string;
  value: string;
}

/**
 * Angular host for the React Tūhono filter panel.
 *
 * Angular keeps ownership of the filter form, FilterService and the calendar
 * view. React only owns the filter UI.
 */
@Component({
  selector: 'app-tuhono-filter-bridge',
  template: '<div class="tuhono-filter-root"></div>',
  styles: [
    `
      :host {
        display: block;
      }

      .tuhono-filter-root {
        display: block;
      }
    `,
  ],
})
export class TuhonoFilterBridgeComponent implements OnInit, OnChanges, OnDestroy {
  @Input() viewMode = 'maramataka';
  @Input() viewModeOptions: TuhonoFilterOption[] = [];

  @Input() contributingOrganisationsOptions: TuhonoFilterOption[] = [];
  @Input() selectedContributingOrganisations: string[] = [];
  @Input() contributingProjectOptions: TuhonoFilterOption[] = [];
  @Input() selectedProjects: string[] = [];
  @Input() locationOptions: TuhonoFilterOption[] = [];
  @Input() selectedLocations: string[] = [];
  @Input() monitoringGroupOptions: TuhonoFilterOption[] = [];
  @Input() selectedMonitoringGroups: string[] = [];
  @Input() monitoringLocationOptions: TuhonoFilterOption[] = [];
  @Input() selectedMonitoringLocations: string[] = [];

  @Input() mataurangaOptions: TuhonoFilterOption[] = [];
  @Input() mataurangaSelection: Record<string, boolean> = {};

  @Input() monitoringOptions: TuhonoFilterOption[] = [];
  @Input() monitoringSelection: Record<string, boolean> = {};

  @Input() calendarType = 'day';
  @Input() calendarTypeOptions: TuhonoFilterOption[] = [];
  @Input() calendarYears: number[] = [];
  @Input() calendarYear = 0;
  @Input() calendarMonths: string[] = [];
  @Input() calendarMonth = 0;
  @Input() calendarWeeks: number[] = [];
  @Input() calendarWeek = 1;
  @Input() calendarDays: number[] = [];
  @Input() calendarDay = 1;

  @Output() viewModeChange = new EventEmitter<string>();
  @Output() contributingOrganisationsChange = new EventEmitter<string[]>();
  @Output() projectsChange = new EventEmitter<string[]>();
  @Output() locationsChange = new EventEmitter<string[]>();
  @Output() monitoringGroupsChange = new EventEmitter<string[]>();
  @Output() monitoringLocationsChange = new EventEmitter<string[]>();
  @Output() mataurangaChange = new EventEmitter<string[]>();
  @Output() monitoringChange = new EventEmitter<string[]>();
  @Output() calendarTypeChange = new EventEmitter<string>();
  @Output() calendarYearChange = new EventEmitter<number>();
  @Output() calendarMonthChange = new EventEmitter<number>();
  @Output() calendarWeekChange = new EventEmitter<number>();
  @Output() calendarDayChange = new EventEmitter<number>();

  private reactRoot: Root | null = null;

  constructor(private el: ElementRef<HTMLElement>, private ngZone: NgZone) {}

  ngOnInit(): void {
    const container = this.el.nativeElement.querySelector('.tuhono-filter-root');
    if (!container) return;

    this.reactRoot = createRoot(container);
    this.renderPanel();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.renderPanel();
  }

  ngOnDestroy(): void {
    this.reactRoot?.unmount();
    this.reactRoot = null;
  }

  private selectedFrom(selection: Record<string, boolean> | null | undefined): string[] {
    return Object.entries(selection || {})
      .filter(([, checked]) => !!checked)
      .map(([key]) => key);
  }

  private runInZone(action: () => void): void {
    this.ngZone.run(action);
  }

  private renderPanel(): void {
    if (!this.reactRoot) return;

    this.reactRoot.render(
      React.createElement(TuhonoFilterPanel, {
        viewMode: this.viewMode,
        viewModeOptions: this.viewModeOptions || [],
        contributingOrganisationsOptions: this.contributingOrganisationsOptions || [],
        selectedContributingOrganisations: this.selectedContributingOrganisations || [],
        contributingProjectOptions: this.contributingProjectOptions || [],
        selectedProjects: this.selectedProjects || [],
        locationOptions: this.locationOptions || [],
        selectedLocations: this.selectedLocations || [],
        monitoringGroupOptions: this.monitoringGroupOptions || [],
        selectedMonitoringGroups: this.selectedMonitoringGroups || [],
        monitoringLocationOptions: this.monitoringLocationOptions || [],
        selectedMonitoringLocations: this.selectedMonitoringLocations || [],
        mataurangaOptions: this.mataurangaOptions || [],
        selectedMatauranga: this.selectedFrom(this.mataurangaSelection),
        monitoringOptions: this.monitoringOptions || [],
        selectedMonitoring: this.selectedFrom(this.monitoringSelection),
        calendarType: this.calendarType,
        calendarTypeOptions: this.calendarTypeOptions || [],
        calendarYears: this.calendarYears || [],
        calendarYear: this.calendarYear,
        calendarMonths: this.calendarMonths || [],
        calendarMonth: this.calendarMonth,
        calendarWeeks: this.calendarWeeks || [],
        calendarWeek: this.calendarWeek,
        calendarDays: this.calendarDays || [],
        calendarDay: this.calendarDay,
        onViewModeChange: (mode: string) => this.runInZone(() => this.viewModeChange.emit(mode)),
        onContributingOrganisationsChange: (selected: string[]) =>
          this.runInZone(() => this.contributingOrganisationsChange.emit(selected)),
        onProjectsChange: (selected: string[]) => this.runInZone(() => this.projectsChange.emit(selected)),
        onLocationsChange: (selected: string[]) => this.runInZone(() => this.locationsChange.emit(selected)),
        onMonitoringGroupsChange: (selected: string[]) =>
          this.runInZone(() => this.monitoringGroupsChange.emit(selected)),
        onMonitoringLocationsChange: (selected: string[]) =>
          this.runInZone(() => this.monitoringLocationsChange.emit(selected)),
        onMataurangaChange: (selected: string[]) => this.runInZone(() => this.mataurangaChange.emit(selected)),
        onMonitoringChange: (selected: string[]) => this.runInZone(() => this.monitoringChange.emit(selected)),
        onCalendarTypeChange: (type: string) => this.runInZone(() => this.calendarTypeChange.emit(type)),
        onCalendarYearChange: (year: number) => this.runInZone(() => this.calendarYearChange.emit(year)),
        onCalendarMonthChange: (month: number) => this.runInZone(() => this.calendarMonthChange.emit(month)),
        onCalendarWeekChange: (week: number) => this.runInZone(() => this.calendarWeekChange.emit(week)),
        onCalendarDayChange: (day: number) => this.runInZone(() => this.calendarDayChange.emit(day)),
      })
    );
  }
}
